'use client';
import React, { useRef, useState } from 'react';
import { IconButton, Typography, Card, Skeleton, Fade } from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
// Import Swiper React components and modules
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';

// Import your components and types
import CardWithModal from './catalog-card-with-modal';
import { ICourseData } from '../../types/course-data';
import { CatalogEventKeys, CatalogEvents } from '../../events';
import { commonSwiperConfig, EventBus } from '@/shared/utils';
import Grid from '@mui/material/Grid2';

interface CatalogSectionProps {
  title: string;
  type: 'slider' | 'grid';
  /** If you want arrow buttons on the slider */
  hasNavigationButtons?: boolean;
  /** Possibly a link icon next to title */
  hasLink?: boolean;
  route?: string;
  items: ICourseData[];
  filteringCatalogData?: boolean;
  loadingCatalogData?: boolean;
  lastCourseElementRef?: (node: HTMLElement | null) => void;
  higlightItems?: boolean;
  eventBus: EventBus<CatalogEvents>;
  onFavoriteChange?: (courseId: string, favouriteId: string | undefined) => void;
  isEnrichingData?: boolean;
  useSkeleton?: boolean; // This will always be true now
}

export const CatalogSection: React.FC<CatalogSectionProps> = ({
  title,
  type,
  items,
  hasNavigationButtons,
  hasLink,
  route,
  filteringCatalogData,
  loadingCatalogData,
  higlightItems,
  lastCourseElementRef,
  eventBus,
  onFavoriteChange,
  isEnrichingData = false,
  useSkeleton = true,
}) => {
  // Create a ref to store the Swiper instance
  const swiperRef = useRef<any>(null);
  // State to track if we are at the beginning or end of the slider
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const handleRedirectCallback = (url: string) => {
    eventBus.emit(CatalogEventKeys.redirects.to, url);
  };

  // Navigation handler for the link icon beside the title
  const handleNavigation = () => {
    if (route) {
      eventBus.emit(CatalogEventKeys.navigate.do, { slug: route });
      console.log(`Navigate to route: ${route}`);
    }
  };

  return (
    <section className="relative mb-8">
      {/* Header Row */}
      <div
        className={`flex flex-wrap items-center gap-2 mb-4 ${
          type === 'grid'
            ? 'justify-center text-center md:text-left md:justify-between'
            : 'justify-between'
        }`}
      >
        <div className="flex items-center gap-x-2">
          <Typography variant="h3" component="p" className="w-full">
            {title}
          </Typography>
          {hasLink && (
            <IconButton onClick={handleNavigation}>
              <ChevronRightRounded />
            </IconButton>
          )}
        </div>

        {type === 'grid' && !loadingCatalogData && items && items.length > 0 && !hasLink && (
          <Typography variant="s1" component="p" className="w-full md:w-auto">
            {items.length} {''}
            {items.length === 1 ? 'Elemento' : 'Elementi'}
          </Typography>
        )}

        {type === 'slider' && hasNavigationButtons && (
          <div className="hidden md:inline-block">
            <div className="flex gap-2">
              <IconButton
                color="inherit"
                onClick={() => swiperRef.current?.slidePrev()}
                disabled={isBeginning}
              >
                <ChevronLeftRounded />
              </IconButton>
              <IconButton
                color="inherit"
                onClick={() => swiperRef.current?.slideNext()}
                disabled={isEnd}
              >
                <ChevronRightRounded />
              </IconButton>
            </div>
          </div>
        )}
      </div>

      {/* GRID LAYOUT */}
      {type === 'grid' && (
        <Grid container spacing={2}>
          {filteringCatalogData &&
            items.length > 0 &&
            items.map((item, idx) => (
              <Grid
                size={{ xs: 12, md: 6, lg: 4 }}
                key={`course-grid-${idx}`}
                className="flex justify-center sm:justify-start"
              >
                <CardWithModal
                  {...item}
                  highlighted={false}
                  viewLessonCallback={handleRedirectCallback}
                  eventBus={eventBus}
                  onFavoriteChange={onFavoriteChange}
                  isEnrichingData={isEnrichingData}
                />
              </Grid>
            ))}

          {loadingCatalogData && (
            <>
              {/* Always show skeleton cards */}
              {Array.from(Array(6)).map((_, i) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`skeleton-${i}`}>
                  <Card
                    className="p-4 bg-neutral-main/10 flex flex-col justify-start w-full h-full max-w-[318px] mx-auto md:max-w-full"
                    sx={{ boxShadow: 'none' }}
                  >
                    <Skeleton
                      sx={{ height: 190 }}
                      animation="wave"
                      variant="rectangular"
                      className="rounded-xl"
                    />
                    <div className="flex flex-col justify-between pt-4 gap-y-4">
                      <div>
                        <Skeleton height={25} className="rounded-lg" />
                        <Skeleton height={25} width="80%" className="rounded-lg" />
                      </div>
                      <div className="flex items-center gap-x-2">
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton height={25} width="100px" className="rounded-lg" />
                      </div>
                    </div>
                  </Card>
                </Grid>
              ))}
            </>
          )}
          {/* Last element ref for infinite scrolling or intersection observers */}
          {items.length > 0 && <div ref={lastCourseElementRef} />}
        </Grid>
      )}

      {type === 'grid' && !loadingCatalogData && items.length === 0 && (
        <Fade in>
          <div className="h-[180px] md:h-[400px] w-full border border-[#414351] border-dotted rounded-xl flex flex-col justify-center items-center text-center relative overflow-hidden">
            <img
              className="hidden md:block absolute h-[320px] md:right-[-16%] md:rotate-[-10deg] lg:rotate-[0deg] lg:right-[24px] xl:right-[10%] opacity-50"
              src="/assets/images/placeholder/placeholder-no-item-dx.png"
              alt="Placeholder Right"
            />
            <img
              className="hidden md:block absolute h-[320px] opacity-50 md:left-[-16%] md:rotate-[10deg] lg:rotate-[0deg] lg:left-[24px] xl:left-[10%]"
              src="/assets/images/placeholder/placeholder-no-item-sx.png"
              alt="Placeholder Left"
            />
            <div className="mb-6">
              <img
                className="h-[40px] md:h-[80px] mx-auto"
                src="/assets/images/placeholder/search-content.svg"
                alt="Search Icon"
              />
              <Typography variant="mac-sans-h4" component="p" className="opacity-30">
                Oops, nessuna corrispondenza.
              </Typography>
              <Typography variant="body-small-500" component="p" className="opacity-30">
                Prova con parole diverse o filtri meno specifici. La musica giusta ti sta
                aspettando.
              </Typography>
            </div>
          </div>
        </Fade>
      )}

      {/* SLIDER LAYOUT */}
      {type === 'slider' && (
        <div className="relative overflow-hidden slider-container">
          {loadingCatalogData && (
            // Skeleton for slider loading state
            <div className="flex gap-4 py-2 overflow-x-hidden">
              {Array.from(Array(4)).map((_, i) => (
                <Card
                  key={`skeleton-slider-${i}`}
                  className="p-4 bg-neutral-main/10 flex-shrink-0 flex flex-col justify-start w-[318px] h-[334px]"
                  sx={{ boxShadow: 'none' }}
                >
                  <Skeleton
                    sx={{ height: 190 }}
                    animation="wave"
                    variant="rectangular"
                    className="rounded-xl"
                  />
                  <div className="flex flex-col justify-between pt-4 gap-y-4">
                    <div>
                      <Skeleton height={25} className="rounded-lg" />
                      <Skeleton height={25} width="80%" className="rounded-lg" />
                    </div>
                    <div className="flex items-center gap-x-2">
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton height={25} width="100px" className="rounded-lg" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {!loadingCatalogData && (
            <Swiper
              {...commonSwiperConfig}
              modules={[Navigation]}
              // Set spacing between slides
              spaceBetween={16}
              // Responsive options for mobile vs. desktop:
              breakpoints={{
                0: {
                  // Mobile: show 1.4 slides so that the first slide is fully visible with 0.4 of the next.
                  slidesPerView: 1.4,
                  centeredSlides: true,
                  centeredSlidesBounds: true,
                },
                768: {
                  // Desktop: show 3.5 slides; no centering so that the slider starts flush left.
                  slidesPerView: 1.5,
                  centeredSlides: false,
                },
                1000: {
                  // Desktop: show 3.5 slides; no centering so that the slider starts flush left.
                  slidesPerView: 2.5,
                  centeredSlides: false,
                },
                1440: {
                  // Desktop: show 3.5 slides; no centering so that the slider starts flush left.
                  slidesPerView: 3.5,
                  centeredSlides: false,
                },
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
              onSlideChange={(swiper) => {
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
            >
              {items.map((item, idx) => (
                <SwiperSlide key={`course-slider-${idx}`}>
                  <div className="course-card">
                    <CardWithModal
                      {...item}
                      highlighted={higlightItems || false}
                      isSlider
                      viewLessonCallback={handleRedirectCallback}
                      eventBus={eventBus}
                      onFavoriteChange={onFavoriteChange}
                      isEnrichingData={isEnrichingData}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {/* Gradient Overlays (visible only when there are slides off-screen) */}
          {!isEnd && !loadingCatalogData && (
            <div className="absolute top-0 right-0 z-20 h-full w-[100px] pointer-events-none bg-gradient-to-r from-transparent to-[#1C1D22]" />
          )}
          {!isBeginning && !loadingCatalogData && (
            <div className="absolute top-0 left-0 z-20 h-full w-[100px] pointer-events-none bg-gradient-to-l from-transparent to-[#1C1D22]" />
          )}

          {!loadingCatalogData && items.length > 0 && <div ref={lastCourseElementRef} />}
        </div>
      )}
    </section>
  );
};
